/**
 * <ToolResultBlock> — collapsed inline view of a `tool_result` block
 * (the result that pairs with a preceding `tool_use` block via
 * `tool_use_id`). Click to expand. Truncates long output.
 *
 * v1: monospace + expandable. Followups: per-tool result formatters
 * (e.g. parse `lx_search` JSON into a hit list, render `lx_read`
 * results with line numbers).
 */
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { ToolResultBlock as ToolResultBlockType } from "@/types/content-blocks";

const PREVIEW_CHARS = 200;

interface ToolResultBlockProps {
  block: ToolResultBlockType;
}

export function ToolResultBlock({ block }: ToolResultBlockProps) {
  const [open, setOpen] = useState(false);
  const isFailed = block.status === "failed";
  const text = block.content ?? "";
  const truncated = text.length > PREVIEW_CHARS;
  const preview = truncated ? `${text.slice(0, PREVIEW_CHARS)}…` : text;
  return (
    <div
      data-testid="tool-result-block"
      data-status={block.status ?? "ok"}
      className={[
        "my-1 rounded-md border text-body-sm",
        isFailed
          ? "border-red-300 bg-red-50/30 dark:border-red-700 dark:bg-red-900/15"
          : "border-cream-300 bg-cream-100/30 dark:border-coffee-700 dark:bg-coffee-900/30",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-caption font-medium uppercase tracking-wide text-coffee-600 dark:text-cream-400"
        aria-expanded={open}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <span>{isFailed ? "Tool result · failed" : "Tool result"}</span>
      </button>
      {!open && text.length > 0 && (
        <div className="px-3 pb-1.5 font-mono text-coffee-500 dark:text-cream-500">
          {preview}
        </div>
      )}
      {open && (
        <pre
          data-testid="tool-result-block-body"
          className="overflow-x-auto whitespace-pre-wrap border-t border-cream-300 px-3 py-2 font-mono text-coffee-700 dark:border-coffee-700 dark:text-cream-300"
        >
          {text}
        </pre>
      )}
    </div>
  );
}
